import { isPlainObject, omit } from 'lodash'

const FORM_WIDE_KEYS = [
  'message',
  '_error',
  'base',
  'fullMessages',
  'full_messages',
]

/** Shown when the server returns no usable error detail (WCAG 3.3.3). */
export const REQUEST_ERROR_FALLBACK =
  'We could not complete this request. Check your connection and try again.'

const SIGN_IN_OBFUSCATED_MESSAGE = 'Invalid email or password.'

const TOKEN_SUGGESTIONS = {
  taken:
    'This value is already in use. If you already have an account, sign in; otherwise try a different value.',
  invalid: 'Check the format of this field and try again.',
  blank: 'This field cannot be empty. Enter a value and try again.',
  empty: 'This field cannot be empty. Enter a value and try again.',
  too_short: 'Enter more characters in this field.',
  too_long: 'Shorten this value and try again.',
  inclusion: 'Select a valid option from the list.',
  not_a_number: 'Enter a number.',
}

function toFieldMessage(val) {
  if (val == null) return ''
  if (Array.isArray(val))
    return val
      .map((v) => toFieldMessage(v))
      .filter(Boolean)
      .join(', ')
  if (isPlainObject(val) && val.message != null)
    return toFieldMessage(val.message)
  return String(val)
}

function looksLikeFullSentence(msg) {
  const t = msg.trim()
  return t.length >= 12 && /\s/.test(t)
}

function isBareToken(msg) {
  const t = msg.trim()
  if (!t) return false
  if (/\s/.test(t)) return false
  return t.length <= 48
}

function sensitiveReplacement(flow) {
  if (flow === 'signIn') return SIGN_IN_OBFUSCATED_MESSAGE
  return REQUEST_ERROR_FALLBACK
}

function suggestsUserEnumeration(lowered) {
  return (
    lowered === 'taken' ||
    lowered.includes('taken') ||
    lowered.includes('not found') ||
    lowered.includes('not_found') ||
    lowered.includes('does not exist') ||
    lowered.includes('could not be found') ||
    lowered.includes('already been taken') ||
    lowered.includes('already registered')
  )
}

/**
 * Maps API / server validation tokens to clearer suggestions when safe (WCAG 3.3.3).
 * @param {string} msg
 * @param {string} _fieldKey
 * @param {{ sensitiveAuth?: boolean, sensitiveAuthFlow?: 'signIn'|'passwordRecovery'|'claim' }} options
 */
export function mapApiMessageString(msg, _fieldKey, options = {}) {
  const { sensitiveAuth = false, sensitiveAuthFlow = 'signIn' } = options
  const trimmed = msg.trim()
  if (!trimmed) return msg

  const lowered = trimmed.toLowerCase()

  if (sensitiveAuth) {
    if (looksLikeFullSentence(trimmed) && !suggestsUserEnumeration(lowered)) {
      return msg
    }
    if (suggestsUserEnumeration(lowered) || isBareToken(trimmed)) {
      return sensitiveReplacement(sensitiveAuthFlow)
    }
    return msg
  }

  const compact = lowered.replace(/\s+/g, '_')
  if (TOKEN_SUGGESTIONS[compact]) return TOKEN_SUGGESTIONS[compact]

  if (lowered.includes('taken') || lowered.includes('already been taken')) {
    return TOKEN_SUGGESTIONS.taken
  }
  if (
    lowered.includes('blank') ||
    lowered.includes('empty') ||
    lowered === "can't be blank"
  ) {
    return TOKEN_SUGGESTIONS.blank
  }
  if (lowered.includes('too short') || lowered.includes('too_short')) {
    return TOKEN_SUGGESTIONS.too_short
  }
  if (lowered.includes('too long') || lowered.includes('too_long')) {
    return TOKEN_SUGGESTIONS.too_long
  }
  if (lowered.includes('invalid')) {
    return TOKEN_SUGGESTIONS.invalid
  }
  if (lowered.includes('inclusion') || lowered.includes('not a valid')) {
    return TOKEN_SUGGESTIONS.inclusion
  }
  if (lowered.includes('not a number') || lowered === 'nan') {
    return TOKEN_SUGGESTIONS.not_a_number
  }

  if (isBareToken(trimmed) && TOKEN_SUGGESTIONS[lowered]) {
    return TOKEN_SUGGESTIONS[lowered]
  }

  return msg
}

function applyMessageMapping(errors, options) {
  const next = { ...errors }
  Object.keys(next).forEach((key) => {
    const val = next[key]
    if (val) next[key] = mapApiMessageString(String(val), key, options)
  })
  return next
}

/**
 * Maps API error payloads (e.g. Rails-style { email: ['taken'] }) into Redux Form's
 * flat string errors object, including optional _error for form-wide copy.
 *
 * @param {*} raw - Value from HttpError.errors (or similar)
 * @param {{ sensitiveAuth?: boolean, sensitiveAuthFlow?: 'signIn'|'passwordRecovery'|'claim' }} [normalizeOptions]
 * @returns {Object} Object suitable for { errors: result } in request onFailure handlers
 */
function normalizeRequestFormErrors(raw, normalizeOptions = {}) {
  const options = {
    sensitiveAuth: false,
    sensitiveAuthFlow: 'signIn',
    ...normalizeOptions,
  }

  if (raw == null || raw === '') {
    return applyMessageMapping({ _error: REQUEST_ERROR_FALLBACK }, options)
  }
  if (typeof raw === 'string') {
    return applyMessageMapping({ _error: raw }, options)
  }
  if (!isPlainObject(raw)) {
    return applyMessageMapping({ _error: String(raw) }, options)
  }

  const fieldSource = omit(raw, FORM_WIDE_KEYS)
  const out = {}

  Object.entries(fieldSource).forEach(([key, val]) => {
    const msg = toFieldMessage(val)
    if (msg) out[key] = msg
  })

  const formWide =
    toFieldMessage(raw._error) ||
    toFieldMessage(raw.message) ||
    toFieldMessage(raw.base) ||
    toFieldMessage(raw.fullMessages) ||
    toFieldMessage(raw.full_messages)

  if (formWide) out._error = formWide

  const fieldKeys = Object.keys(omit(out, ['_error']))
  if (fieldKeys.length === 0 && !out._error) {
    return applyMessageMapping({ _error: REQUEST_ERROR_FALLBACK }, options)
  }

  return applyMessageMapping(out, options)
}

export default normalizeRequestFormErrors
